import { Label } from '@/components/ui/label';
import { Check } from 'lucide-react';
import Illustrations from './StarterKitIllustrations';

/**
 * VisualChoiceCards — large photo-like choice cards.
 * Mobile-first: single column below sm, 2 columns from sm.
 * Selected card gets a thick primary border + ring + check-badge.
 *
 * options: [{ value, label, hint?, illustration: 'meter_p1' | ... }]
 */
export default function VisualChoiceCards({
  label,
  value,
  options,
  onChange,
  testIdPrefix,
  error,
  columns = 2,
}) {
  const grid =
    columns === 3
      ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
      : 'grid-cols-1 sm:grid-cols-2';
  return (
    <div data-testid={`${testIdPrefix}-wrapper`}>
      {label && (
        <Label className="mb-3 block text-base font-semibold text-foreground">
          {label}
        </Label>
      )}
      <div className={`grid ${grid} gap-3 sm:gap-4`}>
        {options.map((opt) => {
          const active = value === opt.value;
          const Illu = opt.illustration ? Illustrations[opt.illustration] : null;
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={[
                'group relative text-left rounded-2xl overflow-hidden border-2 transition-all',
                'bg-white shadow-sm hover:shadow-md',
                active
                  ? 'border-primary ring-4 ring-primary/20 shadow-lg scale-[1.01]'
                  : 'border-border hover:border-primary/40',
                error ? 'border-destructive/60' : '',
              ].join(' ')}
              data-testid={`${testIdPrefix}-${opt.value}`}
              aria-pressed={active}
            >
              {active && (
                <div
                  className="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow-md"
                  aria-hidden="true"
                >
                  <Check className="w-4 h-4" strokeWidth={3} />
                </div>
              )}

              {Illu ? (
                <div className="aspect-[5/4] w-full overflow-hidden bg-[#EEE8DC]">
                  <Illu />
                </div>
              ) : (
                <div className="aspect-[5/4] w-full bg-muted" />
              )}

              <div className="px-4 py-3 sm:px-5 sm:py-4">
                <div className="text-[15px] sm:text-base font-semibold text-foreground leading-snug">
                  {opt.label}
                </div>
                {opt.hint && (
                  <div className="text-[13px] text-muted-foreground mt-1 leading-snug">
                    {opt.hint}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {error && <p className="text-xs text-destructive mt-2">{error}</p>}
    </div>
  );
}
